import { AuthUIWrapper } from "components/Auth/AuthUIWrapper";
import { ConditionalLink } from "components/conditionalLink";
import { Button } from "components/DesignSystem/Button/Button";
import { TextInput } from "components/DesignSystem/TextInput/TextInput";
import { emailSchema } from "formValidations/emailSchema";
import { Form, Formik } from "formik";
import { usePageTitle } from "hooks/usePageTitle";
import Lottie from "lottie-react";
import NotSupportedAnimation from "lottie/NotSupportedAnimation.json";
import { useToast } from "hooks/useToast";
import { useState } from "react";
import { useLocation } from "react-router-dom";
import {
  UnsupportedEntityType,
  useNotifyUnsupportedEntityMutation,
} from "store/apis/unsupportedEntity";

export const NotSupported = () => {
  usePageTitle("Not supported");

  const location = useLocation<{ entityType?: UnsupportedEntityType }>();
  const entityType = location.state?.entityType;

  const [showNotifyForm, setShowNotifyForm] = useState(false);

  const { alertToast } = useToast();
  const [notifyUnsupportedEntity, { isLoading, isSuccess }] =
    useNotifyUnsupportedEntityMutation();

  const handleSubmit = async (values: { email: string }) => {
    if (!entityType) return;
    try {
      await notifyUnsupportedEntity({
        email: values.email,
        entity_type: entityType,
      }).unwrap();
    } catch (error: any) {
      alertToast({ message: error?.data?.error?.message });
    }
  };

  let notifyContent = (
    <Button
      type="button"
      className="t-mt-6"
      size="regular"
      onClick={() => setShowNotifyForm(true)}
    >
      Let me know when it's supported
    </Button>
  );

  if (showNotifyForm && isSuccess) {
    notifyContent = (
      <p className="t-text-green-80 t-text-body-italic t-italic t-mt-6 t-text-center">
        Thanks, we'll reach out once your entity type is supported!
      </p>
    );
  } else if (showNotifyForm) {
    notifyContent = (
      <Formik
        initialValues={{ email: "" }}
        validationSchema={emailSchema()}
        onSubmit={handleSubmit}
      >
        <Form className="t-flex t-items-start t-gap-2 t-mt-6 t-w-full t-max-w-sm">
          <TextInput
            name="email"
            placeholder="Enter your email address"
            type="email"
          />
          <Button
            type="submit"
            size="regular"
            customType="primary"
            isLoading={isLoading}
          >
            Notify me
          </Button>
        </Form>
      </Formik>
    );
  }

  return (
    <AuthUIWrapper>
      <div className="t-flex t-flex-col t-gap-3 t-items-center t-justify-center t-w-full t-mt-20">
        <Lottie
          animationData={NotSupportedAnimation}
          loop={false}
          className="t-size-56"
        />

        <div className="t-flex t-flex-col t-items-center t-justify-center t-w-full">
          <span className="t-text-subtext t-text-text-100">
            Hmm, we might not be the right fit.
          </span>
          <div className="t-text-body-sm t-text-text-30 t-text-center t-mt-2">
            Inkle doesn't currently support S-Corps, LLCs taxed as S-Corps, or
            single-member LLCs - but we'd love to help once we do.
          </div>

          {notifyContent}

          <ConditionalLink to={"https://inkle.ai/"} target="_self">
            <Button
              type="button"
              className="t-mt-6"
              customType="ghost"
              size="small"
            >
              Back to homepage
            </Button>
          </ConditionalLink>
        </div>
      </div>
    </AuthUIWrapper>
  );
};
